import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { FC, KeyboardEventHandler, useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowTrendUp, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { Loading } from "@hostingbv/lib/src/components/Loading";
import { SearchResult } from "@hostingbv/lib/src/api/partner/models/SearchResult";
import { NavLink, useNavigate } from "react-router-dom";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { SectionDivider } from "@hostingbv/lib/src/components/content/SectionDivider";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { Card } from "@hostingbv/lib/src/components/Card";
import { Button } from "../../../lib/src/components/form/Button";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";

export const Search: FC = () => {
  const [scope, setScope] = useState<null | "customers" | "domains" | "sites">(null);
  const [search, setSearch] = useState<string>("");
  const [results, setResults] = useState<SearchResult[]>([])
  const [selectedResult, setSelectedResult] = useState<number>(-1)
  const [requestsLoading, setRequestsLoading] = useState<number>(0)

  useEffect(() => {
    if (search.includes("/c")) {
      setScope("customers");
      setSearch(search.replace("/c", ""));
    } else if (search.includes("/d")) {
      setScope("domains");
      setSearch(search.replace("/d", ""));
    } else if (search.includes("/s")) {
      setScope("sites");
      setSearch(search.replace("/s", ""));
    }
    if (results.length > 0 && search === "") {
      setResults([])
    }
  }, [search, results])

  const onSearchKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if ((e.key === "Backspace" || e.key === "Escape") && search === "" && scope !== null) {
      setScope(null)
    }
    if (results.length > 0) {
      if (e.key === "ArrowDown" && selectedResult < results.length - 1) {
        setSelectedResult(r => r + 1)
      } else if (e.key === "ArrowUp" && selectedResult > 0) {
        setSelectedResult(r => r - 1)
      } else if (e.key === "Enter") {
        navigateToResult(results[selectedResult])
      }
    }
  }

  const navigate = useNavigate()
  const navigateToResult = useCallback((result: SearchResult) => {
    if (result.type === "customer") {
      navigate(`/customers/${result.id}`)
    } else if (result.type === "domain") {
      navigate(`/domains/${result.id}`)
    } else if (result.type === "shared_hosting_subscription") {
      navigate(`/sites/${result.id}`)
    } else if (result.type === "shared_hosting_server") {
      navigate(`/servers/${result.id}`)
    } else {
      console.warn("TODO: Implement navigation for", result.type)
    }
  }, [navigate])

  const debouncedSearch = useDebounce(search.length >= 3 ? search : '', 500)
  const isLoading = requestsLoading > 0
  useEffect(() => {
    if (debouncedSearch !== "") {
      searchRequest(scope, debouncedSearch)
    }
  }, [debouncedSearch, scope])

  const { partnerClient } = useApiClient(config)

  async function searchRequest(scope: null | "customers" | "domains" | "sites", search: string) {
    setRequestsLoading(r => r + 1)
    const type = scope === null ? null : {
      "customers": "customer",
      "domains": "domain",
      "sites": "shared_hosting_subscription",
    }[scope]
    const response = await partnerClient.search(type, search)
    if (response.success) {
      setResults(response.data)
      setSelectedResult(-1)
    }

    setRequestsLoading(r => r - 1)
  }

  return (
    <ContentContainer size={"sm"}>
      <PageIconHeader icon={faSearch}>
        <h1 className={"h-12 flex items-center text-2xl -mt-6 mb-4 font-bold text-test"}>Search</h1>
      </PageIconHeader>
      <label className={"flex flex-col mb-4"}>
        <span className={"text-sm mb-2"}>Search customers or products</span>
        <div className={"bg-white p-1 border rounded-lg border-gray-200 focus-within:border-brand-400 flex items-stretch h-10"}>
          {scope !== null && <button onClick={() => setScope(null)} className={"bg-brand-500 text-white px-2 flex items-center justify-center m-1 text-sm space-x-2 rounded-sm"}>Only:&nbsp;<span className={"capitalize"}>{scope}</span><FontAwesomeIcon icon={faTimes} className={"h-3 w-3"} /></button>}
          <input type="search" autoFocus={true} placeholder={"example.com or Acme inc."} onKeyDown={onSearchKeyDown} value={search} onChange={e => setSearch(e.target.value)} className={"outline-hidden flex-1 mx-2"} />
        </div>
      </label>
      <Loading loading={isLoading}>
        {results.length ? <div className={"border border-gray-200 rounded-lg flex flex-col items-stretch bg-white divide-y divide-gray-200"}>
          {results.map((result, index) => {
            const type = {
              "customer": "Customer",
              "domain": "Domain",
              "shared_hosting_server": "Hosting Server",
              "shared_hosting_subscription": "Site (Shared Hosting)",
            }[result.type]
            return <div className={`p-4 flex flex-row items-stretch ${selectedResult === index ? 'bg-brand-50' : 'hover:bg-brand-50'}`} key={index}>
              <button className={"flex-1 flex flex-col items-start"} onClick={() => navigateToResult(result)}>
                <span className={"font-medium text-slate-800"}>{result.displayName}</span>
                <span className={"text-sm text-slate-500"}>{type}</span>
              </button>
              {result.customerId && result.customerDisplayName ? <NavLink className={"flex-1 flex items-center px-4 text-sm text-slate-500  border border-transparent hover:border-brand-500 rounded-lg"} to={`/customers/${result.customerId}`}>Customer: {result.customerDisplayName}</NavLink> : <></>}
            </div>
          })}
        </div> : <Paragraph>
          Tip: You can search for just customers by typing <code>/c</code> followed by the search term. The same is true for domains, just type <code>/d</code> followed by the search term.
        </Paragraph>}
      </Loading>
      <SectionDivider />
      <DashboardTiles />
    </ContentContainer>
  );
}

const DashboardTiles: FC = () => {
  const navigate = useNavigate()
  const { partnerClient } = useApiClient(config)
  const { resource, loading, reload } = useFetchedResource(() => partnerClient.getDashboardStats())
  useRefreshEffect(() => reload())
  const { can } = usePermissions(partnerClient)
  return <div className={"my-4"}>
    <SectionHeader>Quick links</SectionHeader>
    <div className={"grid grid-cols-2 gap-4"}>
      {can('view', 'domains') && <NavLink to={`/domains`} className={"bg-white hover:bg-brand-50 border border-gray-200 rounded-lg p-4 h-32 flex flex-col items-center justify-center space-y-2"}>
        <Loading loading={loading}>
          <h2 className={"text-xl font-bold"}>{resource?.nDomains ?? ""}</h2>
        </Loading>
        <span className={"text-sm text-slate-500"}>Domains</span>
      </NavLink>}
      {can('view', 'customers') && <NavLink to={`/customers`} className={"bg-white hover:bg-brand-50 border border-gray-200 rounded-lg p-4 h-32 flex flex-col items-center justify-center space-y-2"}>
        <Loading loading={loading}>
          <h2 className={"text-xl font-bold"}>{resource?.nCustomers ?? ""}</h2>
        </Loading>
        <span className={"text-sm text-slate-500"}>Customers</span>
      </NavLink>}
      {can('view', 'servers') && <NavLink to={`/servers`} className={"bg-white hover:bg-brand-50 border border-gray-200 rounded-lg p-4 h-32 flex flex-col items-center justify-center space-y-2"}>
        <Loading loading={loading}>
          <h2 className={"text-xl font-bold"}>{resource?.nHostingServers ?? ""}</h2>
        </Loading>
        <span className={"text-sm text-slate-500"}>Hosting Servers</span>
      </NavLink>}
      {can('view', 'sites') && <NavLink to={`/sites`} className={"bg-white hover:bg-brand-50 border border-gray-200 rounded-lg p-4 h-32 flex flex-col items-center justify-center space-y-2"}>
        <Loading loading={loading}>
          <h2 className={"text-xl font-bold"}>{resource?.nHostingSubscriptions ?? ""}</h2>
        </Loading>
        <span className={"text-sm text-slate-500"}>Sites</span>
      </NavLink>}
    </div>
    {can('view', 'contracts') && <>
      <div className={"mt-12 relative"}>
        <span className={"absolute -top-2 right-6 h-4 bg-brand-700 text-brand-50 font-medium tracking-wide flex items-center uppercase text-xs px-2 rounded-sm"}>New</span>
        <Card title={'Domain price index'}>
          <p className={"mb-4"}>
            We have gathered domain prices from several prominent sellers and put them in an handy overview for you.
          </p>
          <Button type={'secondary'} size={'sm'} text={'View domain prices'} icon={faArrowTrendUp} onClick={() => navigate('/domains/prices')} />
        </Card>

      </div>
    </>}
  </div>
}


function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}
